import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, ArrowLeft, Mail, Lock, Eye, EyeOff, Target, CheckCircle, AlertCircle, User, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlayerJoinPage = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // User exists state - show login form instead of registration
  const [userExists, setUserExists] = useState(false);
  const [existingUserName, setExistingUserName] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);
  
  const [form, setForm] = useState({
    parent_name: '',
    parent_email: '',
    password: '',
    athlete_name: ''
  });

  useEffect(() => {
    decodeInvite();
  }, [inviteCode]);

  const decodeInvite = () => {
    try {
      const decoded = JSON.parse(atob(inviteCode));
      setInviteData(decoded);
      setLoading(false);
    } catch (err) {
      setError('Invalid or expired invite link');
      setLoading(false);
    }
  };

  // Check if email exists when user types
  const checkEmailExists = async (email) => {
    if (!email || !email.includes('@')) return;
    
    setCheckingEmail(true);
    try {
      const response = await axios.get(`${API}/clubs/player/check-email/${encodeURIComponent(email)}`);
      if (response.data.user_exists) {
        setUserExists(true);
        setExistingUserName(response.data.user_name || '');
      } else {
        setUserExists(false);
        setExistingUserName('');
      }
    } catch (error) {
      // If endpoint doesn't exist or error, continue with registration
      setUserExists(false);
    }
    setCheckingEmail(false);
  };

  // Debounce email check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (form.parent_email) {
        checkEmailExists(form.parent_email);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.parent_email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (userExists) {
        // Existing user - login and add athlete
        const response = await axios.post(`${API}/clubs/player/accept-invite-existing`, {
          email: form.parent_email,
          password: form.password,
          athlete_name: form.athlete_name || undefined,
          team_id: inviteData.teamId,
          club_id: inviteData.clubId
        });
        
        // Auto-login with returned token
        if (response.data.token) {
          login(response.data.token, response.data.user);
          toast.success('Player added to your account!');
          navigate('/dashboard');
          return;
        }
        
        setSuccess(true);
        toast.success('Player added successfully!');
      } else {
        // New user - register
        const response = await axios.post(`${API}/clubs/player/register`, {
          parent_name: form.parent_name,
          parent_email: form.parent_email,
          password: form.password,
          athlete_name: form.athlete_name || undefined,
          team_id: inviteData.teamId,
          club_id: inviteData.clubId
        });
        
        setSuccess(true);
        toast.success('Account created successfully!');
      }
    } catch (error) {
      // Check if error is because user exists (409 Conflict)
      if (error.response?.status === 409) {
        const detail = error.response?.data?.detail;
        if (detail?.code === 'USER_EXISTS') {
          setUserExists(true);
          setExistingUserName(detail.user_name || '');
          toast.info('Account found! Please enter your password to add this player.');
          setSubmitting(false);
          return;
        }
      }
      
      const message = error.response?.data?.detail || 'Failed to complete registration';
      toast.error(typeof message === 'string' ? message : message.message || 'Error');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="mx-auto text-red-400 mb-4" size={48} />
          <h1 className="font-heading text-2xl uppercase mb-2">Invalid Invite</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-blue-500 text-white hover:bg-blue-400">
              Go to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-emerald-400" size={40} />
          </div>
          <h1 className="font-heading text-3xl uppercase mb-4">
            Welcome to <span className="text-blue-400">{inviteData?.teamName}</span>!
          </h1>
          <p className="text-muted-foreground mb-8">
            {userExists 
              ? 'Player has been added to your account. You can now access the team from your dashboard.'
              : 'Your account has been created. You can now access the team and track your player\'s development.'
            }
          </p>
          <div className="space-y-3">
            <Link to="/login">
              <Button className="w-full bg-blue-500 text-white hover:bg-blue-400">
                Login to Dashboard
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full border-border">
                Go to Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-8"
        >
          <ArrowLeft size={18} />
          <span className="text-sm uppercase tracking-widest">Back to Home</span>
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className={`w-16 h-16 ${userExists ? 'bg-emerald-500/20' : 'bg-blue-500/20'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            {userExists ? (
              <LogIn className="text-emerald-400" size={32} />
            ) : (
              <Target className="text-blue-400" size={32} />
            )}
          </div>
          <h1 className="font-heading text-2xl uppercase mb-2">
            {userExists ? (
              <>Welcome back, <span className="text-emerald-400">{existingUserName || 'User'}</span>!</>
            ) : (
              <>Join <span className="text-blue-400">Team</span></>
            )}
          </h1>
          <p className="text-muted-foreground text-sm">
            {userExists 
              ? 'Enter your password to add this player to your account'
              : 'Register as a parent/player'
            }
          </p>
        </motion.div>

        {/* User Exists Alert */}
        {userExists && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-emerald-500/10 border border-emerald-500/30 rounded-sm p-4 mb-6"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="text-emerald-400 flex-shrink-0" size={20} />
              <div>
                <p className="text-sm font-medium text-emerald-400">Account Found!</p>
                <p className="text-xs text-muted-foreground">
                  We found an existing account with this email. Just enter your password to add this player.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Invite Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border border-border/40 rounded-sm p-4 mb-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <Users className="text-emerald-400" size={20} />
            <span className="text-sm text-muted-foreground">Team Information</span>
          </div>
          <div className="space-y-2 text-sm">
            {inviteData?.clubName && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Club:</span>
                <span className="font-medium">{inviteData.clubName}</span>
              </div>
            )}
            {inviteData?.teamName && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Team:</span>
                <span className="font-medium text-blue-400">{inviteData.teamName}</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Registration/Login Form */}
        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-card border border-border/40 rounded-sm p-6 space-y-5"
          data-testid="player-join-form"
        >
          {/* Only show name field for new users */}
          {!userExists && (
            <div>
              <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                Parent/Guardian Name *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  type="text"
                  value={form.parent_name}
                  onChange={(e) => setForm({...form, parent_name: e.target.value})}
                  required={!userExists}
                  className="bg-transparent border-border pl-10 focus:border-blue-500"
                  placeholder="Your full name"
                  data-testid="player-parent-name-input"
                />
              </div>
            </div>
          )}

          <div>
            <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
              Player Name
            </label>
            <div className="relative">
              <Target className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                type="text"
                value={form.athlete_name}
                onChange={(e) => setForm({...form, athlete_name: e.target.value})}
                className="bg-transparent border-border pl-10 focus:border-blue-500"
                placeholder="Player's full name"
                data-testid="player-athlete-name-input"
              />
            </div>
          </div>

          <div>
            <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
              Email *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                type="email"
                value={form.parent_email}
                onChange={(e) => setForm({...form, parent_email: e.target.value})}
                required
                className={`bg-transparent border-border pl-10 focus:border-blue-500 ${userExists ? 'border-emerald-500/50' : ''}`}
                placeholder="your@email.com"
                data-testid="player-email-input"
              />
              {checkingEmail && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
              {userExists ? 'Your Password *' : 'Create Password *'}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm({...form, password: e.target.value})}
                required
                minLength={6}
                className="bg-transparent border-border pl-10 pr-10 focus:border-blue-500"
                placeholder={userExists ? "Enter your password" : "Min 6 characters"}
                data-testid="player-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-3">
            <p className="text-xs text-blue-400 font-medium mb-1">What you'll get access to:</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• View training sessions and schedules</li>
              <li>• Track player development and progress</li>
              <li>• Receive team announcements</li>
              <li>• Confirm attendance for sessions</li>
            </ul>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className={`w-full ${userExists ? 'bg-emerald-500 hover:bg-emerald-400' : 'bg-blue-500 hover:bg-blue-400'} text-white uppercase tracking-wider py-6 font-bold`}
            data-testid="player-join-submit-btn"
          >
            {submitting 
              ? (userExists ? 'Adding Player...' : 'Creating Account...') 
              : (userExists ? 'Login & Add Player' : 'Join Team')
            }
          </Button>
        </motion.form>

        {/* Reset to new account option */}
        {userExists && (
          <div className="mt-4 text-center">
            <button 
              onClick={() => {
                setUserExists(false);
                setExistingUserName('');
                setForm({...form, password: ''});
              }}
              className="text-sm text-muted-foreground hover:text-white transition-colors"
            >
              Not {existingUserName}? <span className="text-blue-400">Create new account</span>
            </button>
          </div>
        )}

        {/* Already have account - only for new users */}
        {!userExists && (
          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>
              Already have an account?{' '}
              <Link to="/login" className="text-blue-400 hover:underline">
                Login here
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerJoinPage;
